import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import DatePicker from 'react-datepicker';
import Button from '@mui/material/Button';
import DownloadIcon from '@mui/icons-material/Download';
import PrintIcon from '@mui/icons-material/Print';
import CircularProgress from '@mui/material/CircularProgress';
import 'react-datepicker/dist/react-datepicker.css';
import './DataTable.css';

// Styled components for the table
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

export default function DataTable() {
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const tableRef = useRef(null);

  // Fetch filtered data from the backend
  const fetchFilteredData = async () => {
    if (!startDate || !endDate) return;
    
    setLoading(true);
    try {
      const response = await fetch(
        process.env.REACT_APP_ENDPOINT_HTTP + `/api/power/range?start=${startDate.toISOString()}&end=${endDate.toISOString()}`
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      const formattedData = data.map((item) => createData(
        new Date(item.timestamp).toLocaleString(),
        item.voltage,
        item.current,
        item.power,
        item.energy,
        item.frequency,
        item.powerFactor
      ));

      setRows(formattedData);
    } catch (error) {
      console.error('Error fetching filtered data:', error);
    } finally {
      setLoading(false);
    }
  };

  // WebSocket connection for real-time updates
  useEffect(() => {
    const socket = new WebSocket(process.env.REACT_APP_ENDPOINT_WS);
    
    socket.onmessage = (event) => {
      const newData = JSON.parse(event.data);
      setRows((prevRows) => [
        createData(
          new Date(newData.timestamp).toLocaleString(),
          newData.voltage,
          newData.current,
          newData.power,
          newData.energy,
          newData.frequency,
          newData.powerFactor
        ),
        ...prevRows
      ]);
    };

    socket.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    return () => socket.close();
  }, []);

  // Helper function to create row data
  function createData(timestamp, voltage, current, power, energy, frequency, powerFactor) {
    const dateTime = timestamp;

    return {
      dateTime,
      voltage,
      current,
      power,
      energy,
      frequency,
      powerFactor
    };
  }

  // Print function
  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    const tableHTML = tableRef.current.innerHTML;
    
    printWindow.document.write(`
      <html>
        <head>
          <title>Filtered Data Table</title>
          <style>
            table { border-collapse: collapse; width: 100%; }
            th, td { border: 1px solid black; padding: 8px; text-align: center; }
            th { background-color: #000; color: white; }
          </style>
        </head>
        <body>
          <div class="print-content">
            ${tableHTML}
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  // CSV export function
  const exportToCSV = () => {
    const headers = [
      'Date/Time',
      'Voltage (V)',
      'Current (A)',
      'Power (W)',
      'Energy (kWh)',
      'Frequency (Hz)',
      'Power Factor (PF)'
    ];

    const csvData = rows.map(row => {
      return [
        row.dateTime,
        row.voltage,
        row.current,
        row.power,
        row.energy,
        row.frequency,
        row.powerFactor
      ];
    });

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `data_export_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="project">
      <div className="table">
        <section className="cards grid3">
          <div className="cardBox">
            <div className="datePickerBox no-print">
              <div className="leftSection">
                <DatePicker
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  placeholderText="Start date"
                  className="datePicker"
                  dateFormat="MM/dd/yyyy"
                />
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  placeholderText="End date"
                  className="datePicker"
                  dateFormat="MM/dd/yyyy"
                />
                <Button 
                  variant="contained" 
                  color="primary" 
                  onClick={fetchFilteredData} 
                  disabled={!startDate || !endDate || loading}
                  className="filter-button"
                >
                  {loading ? 'Loading...' : 'Filter'}
                </Button>
              </div>

              <div className="rightSection">
                <Button 
                  variant="contained" 
                  color="secondary" 
                  onClick={handlePrint}
                  startIcon={<PrintIcon />}
                  disabled={rows.length === 0}
                  className="action-button"
                >
                  Print
                </Button>
                <Button 
                  variant="contained" 
                  color="success" 
                  onClick={exportToCSV}
                  startIcon={<DownloadIcon />}
                  disabled={rows.length === 0}
                  className="action-button"
                >
                  CSV
                </Button>
              </div>
            </div>

            <div className="tableBox" ref={tableRef}> 
              <TableContainer component={Paper} className="tableContainer">
                {loading && (
                  <div className="loadingOverlay">
                    <CircularProgress />
                  </div>
                )}
                <Table sx={{ minWidth: 650 }} aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell align="center">Date/Time</StyledTableCell>
                      <StyledTableCell align="center">Voltage (V)</StyledTableCell>
                      <StyledTableCell align="center">Current (A)</StyledTableCell>
                      <StyledTableCell align="center">Power (W)</StyledTableCell>
                      <StyledTableCell align="center">Energy (kWh)</StyledTableCell>
                      <StyledTableCell align="center">Frequency (Hz)</StyledTableCell>
                      <StyledTableCell align="center">Power Factor</StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <StyledTableRow key={`${row.dateTime}-${index}`}>
                        <StyledTableCell component="th" scope="row" align="center">
                          {row.dateTime}
                        </StyledTableCell>
                        <StyledTableCell align="center">{row.voltage}</StyledTableCell>
                        <StyledTableCell align="center">{row.current}</StyledTableCell>
                        <StyledTableCell align="center">{row.power}</StyledTableCell>
                        <StyledTableCell align="center">{row.energy}</StyledTableCell>
                        <StyledTableCell align="center">{row.frequency}</StyledTableCell>
                        <StyledTableCell align="center">{row.powerFactor}</StyledTableCell>
                      </StyledTableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}